import { Box, Chip, Typography } from "@mui/material";
import { ReactNode } from "react";

import { resolveLearningMediaUrl } from "../../lib/learning/mediaReference";
import { borderRadius, color, sessionStatusStyle, shadows, typography } from "../../theme/designTokens";
import { ActivityThumbnail } from "../ActivityThumbnail";

// Shared presentation primitives for the learning admin pages (grants and
// assignments), so both render catalog content in the same card language.
// Spacing uses plain MUI units (1 = 8px) rather than the `space` tokens: those
// tokens are raw pixel values, and MUI multiplies numeric `sx` spacing by 8,
// which is what inflated these pages to a full screen of padding.

export const THUMBNAIL_WIDTH = 104;
export const THUMBNAIL_HEIGHT = 68;

export const grantedStyle = sessionStatusStyle.done;

export const panelSx = {
  border: `1px solid ${color.panelBorder}`,
  borderRadius: borderRadius.sm,
  backgroundColor: color.white,
  boxShadow: shadows.card,
};

export const itemCardSx = (highlighted: boolean) => ({
  px: 2,
  py: 1.75,
  border: `1px solid ${highlighted ? color.plannedBorder : color.panelBorder}`,
  borderRadius: borderRadius.sm,
  backgroundColor: highlighted ? color.plannedBackground : color.white,
  boxShadow: shadows.card,
  transition: "box-shadow 150ms ease, border-color 150ms ease",
  "&:hover": { boxShadow: shadows.secondary },
});

export const matchesSearch = (search: string, values: string[]): boolean => {
  const normalizedSearch = search.trim().toLocaleLowerCase("de-DE");
  if (!normalizedSearch) return true;
  return values.some(value => value.toLocaleLowerCase("de-DE").includes(normalizedSearch));
};

// Small uppercase label, matching the eyebrow treatment in ActivityRowHeader.
export const Eyebrow = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{
      fontSize: typography.fontSize.xxs,
      fontWeight: typography.fontWeight.semibold,
      color: "text.secondary",
      letterSpacing: typography.letterSpacing.md,
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

// One metadata fact (lesson count, duration, …). The label stays the sole text
// child of its own element so it reads as a single value, not a merged line.
export const MetaItem = ({ icon, label }: { icon: ReactNode; label: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
    {icon}
    <Typography variant="body2" sx={{ fontSize: typography.fontSize.xs }}>
      {label}
    </Typography>
  </Box>
);

export const metaIconSx = { fontSize: 17 };

export const metaChipSx = {
  height: 22,
  backgroundColor: color.background2,
  border: `1px solid ${color.panelBorder}`,
  fontSize: typography.fontSize.xxs,
};

export const MetadataChips = ({ tags }: { tags: string[] }) => (
  <>
    {tags.map(tag => (
      <Chip key={tag} label={tag} size="small" sx={metaChipSx} />
    ))}
  </>
);

export const ItemThumbnail = ({ thumbnailFilename }: { thumbnailFilename: string | null }) => (
  <ActivityThumbnail
    thumbnailFilename={thumbnailFilename}
    cmsContentKind={null}
    resolveThumbnailUrl={resolveLearningMediaUrl}
    width={THUMBNAIL_WIDTH}
    height={THUMBNAIL_HEIGHT}
    iconSize={30}
  />
);

export const ItemDescription = ({ description }: { description: string }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{
      fontSize: typography.fontSize.xs,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }}
  >
    {description}
  </Typography>
);

export const MetaRow = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 0.75 }}>{children}</Box>
);

export const ListMessage = ({ message }: { message: string }) => (
  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
    {message}
  </Typography>
);
